// apps/web/src/pages/pcp/conferencia-pv/use-recebimento-pv.ts — Fase 12, Planos 10 e 29 (NFE-05, NFE-06, T-12-36).
// Carrega a sugestão FIFO por item da nota (`GET /vendas/recebimentos/:id/sugestoes`, pv:ler) e envia a confirmação:
// com algum item de PV escolhido usa `POST /vendas/recebimentos/:id/confirmar` (Remessa + Lote + OP); sem nenhum, a rota legada.
// 403 ou erro ao consultar as sugestões só esconde o campo "Item do PV": a conferência legada segue igual.
import { useEffect, useState } from 'react';
import type { CandidatoItemPv, ResultadoRecebimentoPv, SugestaoRecebimentoItem } from '@pieltec/contracts';
import { apiMutate } from '../../../lib/csrf-fetch';
import {
  candidatosPorItemDasSugestoes,
  montarItensConfirmacao,
  selecaoInicialDasSugestoes,
  temAlocacao,
  type AlocacaoEmEdicao,
  type ContextoConfirmacao,
  type ItemConferido,
} from '../../../lib/vendas/recebimento-pv';

export interface CandidatosDaNota {
  /** True desde o instante em que a nota muda até as sugestões chegarem (a confirmação fica bloqueada nesse intervalo). */
  readonly carregando: boolean;
  /** False quando as sugestões não puderam ser lidas (sem `pv:ler`, erro): o campo "Item do PV" fica oculto. */
  readonly disponivel: boolean;
  readonly candidatosPorItem: Readonly<Record<string, readonly CandidatoItemPv[]>>;
  /** Seleção inicial (FIFO ou pré-alocação da nota) para o estado da tela. */
  readonly selecaoInicial: Readonly<Record<string, string>>;
  /** Divisões pré-preenchidas quando a sugestão reparte a quantidade entre vários itens de PV. */
  readonly divisoesIniciais: Readonly<Record<string, readonly AlocacaoEmEdicao[]>>;
}

const VAZIO: CandidatosDaNota = { carregando: false, disponivel: false, candidatosPorItem: {}, selecaoInicial: {}, divisoesIniciais: {} };
const CARREGANDO: CandidatosDaNota = { ...VAZIO, carregando: true };

export function useCandidatosDaNota(documentoFiscalId: string | null): CandidatosDaNota {
  // `paraDocumento` amarra o estado à nota: ao trocar de nota o retorno já é "carregando" (sem um render com sugestões da nota
  // anterior), o que evita confirmar cedo demais e misturar a seleção inicial de duas notas.
  const [interno, setEstado] = useState<{ readonly paraDocumento: string | null; readonly estado: CandidatosDaNota }>({
    paraDocumento: null,
    estado: VAZIO,
  });
  const estado = interno.paraDocumento === documentoFiscalId ? interno.estado : documentoFiscalId === null ? VAZIO : CARREGANDO;

  useEffect(() => {
    if (!documentoFiscalId) {
      setEstado({ paraDocumento: null, estado: VAZIO });
      return;
    }
    let vivo = true;
    setEstado({ paraDocumento: documentoFiscalId, estado: CARREGANDO });
    void (async () => {
      try {
        const res = await fetch(`/api/v1/vendas/recebimentos/${encodeURIComponent(documentoFiscalId)}/sugestoes`, { credentials: 'include' });
        if (!res.ok) {
          if (vivo) setEstado({ paraDocumento: documentoFiscalId, estado: VAZIO });
          return;
        }
        const sugestoes = (await res.json()) as SugestaoRecebimentoItem[];
        const { selecao, divisoes } = selecaoInicialDasSugestoes(sugestoes);
        if (vivo) {
          setEstado({
            paraDocumento: documentoFiscalId,
            estado: { carregando: false, disponivel: true, candidatosPorItem: candidatosPorItemDasSugestoes(sugestoes), selecaoInicial: selecao, divisoesIniciais: divisoes },
          });
        }
      } catch {
        if (vivo) setEstado({ paraDocumento: documentoFiscalId, estado: VAZIO });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [documentoFiscalId]);

  return estado;
}

export type ResultadoConfirmacao =
  | { readonly ok: true; /** Presente só na rota de PV (a rota legada não devolve OP). */ readonly resultado: ResultadoRecebimentoPv | null }
  | { readonly ok: false; readonly message: string };

const MENSAGEM_FALHA_CONFIRMAR = 'Falha ao confirmar recebimento físico.';

async function mensagemDaFalha(res: Response): Promise<string> {
  try {
    const corpo = (await res.json()) as { message?: unknown };
    if (Array.isArray(corpo.message) && typeof corpo.message[0] === 'string' && corpo.message[0]) return corpo.message[0];
    if (typeof corpo.message === 'string' && corpo.message.trim() !== '') return corpo.message;
  } catch {
    // Corpo vazio ou não-JSON: mensagem genérica.
  }
  return MENSAGEM_FALHA_CONFIRMAR;
}

/** Confirma o recebimento físico: rota de PV quando há item de PV escolhido, senão a rota legada (GOV-02). */
export async function confirmarRecebimentoDaNota(
  documentoFiscalId: string,
  itens: readonly ItemConferido[],
  selecao: Readonly<Record<string, string>>,
  contexto?: ContextoConfirmacao,
): Promise<ResultadoConfirmacao> {
  const id = encodeURIComponent(documentoFiscalId);
  const comPv = temAlocacao(selecao, itens);
  const res = comPv
    ? await apiMutate(`/api/v1/vendas/recebimentos/${id}/confirmar`, { method: 'POST', body: { itens: montarItensConfirmacao(selecao, itens, contexto) } })
    : await apiMutate(`/api/v1/documentos-fiscais/${id}/confirmar`, { method: 'POST', body: { itens } });
  if (!res.ok) return { ok: false, message: await mensagemDaFalha(res) };
  return { ok: true, resultado: comPv ? ((await res.json().catch(() => null)) as ResultadoRecebimentoPv | null) : null };
}
